/**
 * 承認案件一覧で、 承認・差戻しが実行されなかった理由の分類 (AC-APT-022)。
 *
 * 画面の振る舞いは理由で 3 つに分かれる (`DecisionFailureReason`):
 * - 入札締切 (スポット・ブロック) / GC (時間前) を過ぎていた (承認・差戻し) → ダイアログを閉じ、
 *   一覧を読み込み直して、 一覧の画面にエラーとして出す
 * - 入札制限違反 / 自動取消時刻超過 (承認のみ) → ダイアログを開いたまま、 コメントを保持する
 * - それ以外 (通信エラー・サーバーエラー、 AC-APT-012) → 従来どおりトーストで案内する
 *
 * 「締切 / GC を過ぎていた時は入札制限違反・自動取消時刻超過を出さない」 は、 backend が GC を
 * 入札制限・自動取消時刻より先に判定して 1 つの理由だけを返すこと (時間前入札)、 スポット・
 * ブロックは RPC の前に締切を判定すること (`isPastSpotDeadline`) で成り立つ。
 *
 * 判別できる理由は backend が `google.rpc.ErrorInfo` で返す reason に限る
 * (時間前入札は GC / 自動取消時刻 / 入札制限、 スポットは締切・入札制限・コマ計。 ブロックは締切・入札制限・コマ計)。
 * スポット・ブロックの入札締切後の承認・差戻しを backend が拒否する (#6647) までは、
 * 締切後の判定は画面側の時刻判定が担う。
 */
import { ConnectError } from '@connectrpc/connect'
import { BusinessProcess } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { ApprovalTask } from '@ebalance/proto/bffuser/v1/approval_task_model_pb'
import {
  BlockBidWriteBlockedReason,
  BlockBidWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  IntradayOrderWriteBlockedReason,
  IntradayOrderWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import {
  SpotBidWriteBlockedReason,
  SpotBidWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import type { TFunction } from 'i18next'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { fromProtoDate } from '~/models/dateTime'
import { intradayBlockedReasonMessage } from '~/models/intradayServerError'
import { blockWriteBlockedReasonMessage } from '~/pages/SpotBidding/models/blockBidWrite'
import {
  calcSpotPhase,
  isSpotBeforeDeadline,
} from '~/pages/SpotBidding/models/marketPhase'
import { spotWriteBlockedReasonMessage } from '~/pages/SpotBidding/models/spotBidWrite'

export type DecisionAction = 'approve' | 'reject'

/** 承認・差戻しが実行されなかった理由。 */
export type DecisionFailureReason =
  /** (a) スポット (ブロックを含む) の入札締切を過ぎていた。 承認・差戻し。 */
  | { kind: 'spotDeadlinePassed' }
  /** (b) 時間前入札の対象コマの GC を過ぎていた。 承認・差戻し。 */
  | { kind: 'intradayGcPassed' }
  /** (c) 入札制限に違反していた。 承認のみ。 `message` は理由だけを伝える文 (明細が取れない時の表示)。 */
  | { kind: 'bidLimitViolation'; message: string }
  /** (d) 時間前入札の自動取消時刻を過ぎていた。 承認のみ。 */
  | { kind: 'autoCancelPassed'; message: string }
  /** 上のどれでもない失敗。 `message` は AC-APT-012 の操作別の文言、 または理由つきの文言。 */
  | { kind: 'other'; message: string }

/**
 * スポット・ブロックの入札締切 (受渡日前日 10:00 JST) を `now` の時点で過ぎているか。
 * 受渡日が分からない案件は過ぎていないものとして扱う (画面側では止めず、 backend に任せる)。
 */
export function isPastSpotDeadline(task: ApprovalTask, now: Date): boolean {
  if (task.targetDate === undefined) return false
  const isSpotFamily =
    task.businessProcess === BusinessProcess.SPOT_BID ||
    task.businessProcess === BusinessProcess.BLOCK_BID
  if (!isSpotFamily) return false
  return !isSpotBeforeDeadline(
    calcSpotPhase(now, fromProtoDate(task.targetDate)),
  )
}

/** 承認・差戻しの失敗を、 画面の振る舞いを決める理由に分類する。 */
export function classifyDecisionFailure(
  task: ApprovalTask,
  action: DecisionAction,
  err: unknown,
  t: TFunction,
): DecisionFailureReason {
  const generic = (): DecisionFailureReason => ({
    kind: 'other',
    message: t(
      action === 'approve'
        ? 'page:adminApprovalTasks.toast.approveError'
        : 'page:adminApprovalTasks.toast.rejectError',
    ),
  })
  if (!(err instanceof ConnectError)) return generic()
  const isApprove = action === 'approve'

  switch (task.businessProcess) {
    case BusinessProcess.SPOT_BID: {
      const info = readAppErrorInfo(err, SpotBidWriteBlockedReasonSchema)
      if (info?.reason === SpotBidWriteBlockedReason.GATE_CLOSURE) {
        return { kind: 'spotDeadlinePassed' }
      }
      if (
        isApprove &&
        (info?.reason === SpotBidWriteBlockedReason.BID_LIMIT_VIOLATION ||
          info?.reason ===
            SpotBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION)
      ) {
        const message = spotWriteBlockedReasonMessage(err, t)
        if (message !== undefined) return { kind: 'bidLimitViolation', message }
      }
      return generic()
    }
    case BusinessProcess.BLOCK_BID: {
      const info = readAppErrorInfo(err, BlockBidWriteBlockedReasonSchema)
      // 締切後の拒否 (main で BLOCK_BID_WRITE_BLOCKED_REASON_GATE_CLOSURE が入った)。画面の事前判定をすり抜けた時もスポットと同じ扱い
      if (info?.reason === BlockBidWriteBlockedReason.GATE_CLOSURE) {
        return { kind: 'spotDeadlinePassed' }
      }
      if (
        isApprove &&
        (info?.reason === BlockBidWriteBlockedReason.BID_LIMIT_VIOLATION ||
          info?.reason ===
            BlockBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION)
      ) {
        const message = blockWriteBlockedReasonMessage(err, t)
        if (message !== undefined) return { kind: 'bidLimitViolation', message }
      }
      return generic()
    }
    case BusinessProcess.INTRADAY_BID: {
      const info = readAppErrorInfo(err, IntradayOrderWriteBlockedReasonSchema)
      const message = intradayBlockedReasonMessage(err, t)
      switch (info?.reason) {
        case IntradayOrderWriteBlockedReason.APPROVAL_GC_PASSED:
          return { kind: 'intradayGcPassed' }
        case IntradayOrderWriteBlockedReason.APPROVAL_WITHDRAW_AT_PASSED:
          return message === undefined || !isApprove
            ? generic()
            : { kind: 'autoCancelPassed', message }
        case IntradayOrderWriteBlockedReason.BID_LIMIT_VIOLATION:
        case IntradayOrderWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION:
          return message === undefined || !isApprove
            ? generic()
            : { kind: 'bidLimitViolation', message }
        default:
          // 他の理由 (受付開始前など) は理由つきの文言があればそれを出す (AC-APT-012 の汎用文言より優先)。
          return message !== undefined ? { kind: 'other', message } : generic()
      }
    }
    default:
      return generic()
  }
}

/** (a)(b) の文言。 一覧の画面にエラーとして出す。 */
export function closedDecisionMessage(
  reason: Extract<
    DecisionFailureReason,
    { kind: 'spotDeadlinePassed' | 'intradayGcPassed' }
  >,
  t: TFunction,
): string {
  return reason.kind === 'spotDeadlinePassed'
    ? t('page:adminApprovalTasks.decisionBlocked.spotDeadlinePassed')
    : t('page:adminApprovalTasks.decisionBlocked.intradayGcPassed')
}
